import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControl,
  FormLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import DatePickerComponent from "../DatePickerComponent";
import TimePickerComponent from "../TimePickerComponent";
import { getDateRangeError, getTimeRangeError } from "./availabilityValidation";
import { STATUS_OPTIONS } from "./statusConfig";

const BulkApplyDrawer = ({ open, onClose, linguists, onApply, applying }) => {
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState(dayjs().format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(dayjs().add(7, "day").format("YYYY-MM-DD"));
  const [timeFrom, setTimeFrom] = useState("");
  const [timeTo, setTimeTo] = useState("");
  const [dateToError, setDateToError] = useState("");
  const [timeToError, setTimeToError] = useState("");

  const toggleUser = id => {
    setSelectedUserIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedUserIds.length === linguists.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(linguists.map(l => l.id));
    }
  };

  const handleDateFromChange = e => {
    const nextFrom = e.target.value;
    setDateFrom(nextFrom);
    setDateToError(getDateRangeError(nextFrom, dateTo));
  };

  const handleDateToChange = e => {
    const nextTo = e.target.value;
    setDateTo(nextTo);
    setDateToError(getDateRangeError(dateFrom, nextTo));
  };

  const handleTimeFromChange = e => {
    const nextFrom = e.target.value;
    setTimeFrom(nextFrom);
    setTimeToError(getTimeRangeError(nextFrom, timeTo));
  };

  const handleTimeToChange = e => {
    const nextTo = e.target.value;
    setTimeTo(nextTo);
    setTimeToError(getTimeRangeError(timeFrom, nextTo));
  };

  const handleApply = () => {
    if (!selectedUserIds.length || !status || !dateFrom || !dateTo) return;

    const nextDateToError = getDateRangeError(dateFrom, dateTo);
    const nextTimeToError = status === "CUSTOM" ? getTimeRangeError(timeFrom, timeTo) : "";
    setDateToError(nextDateToError);
    setTimeToError(nextTimeToError);
    if (nextDateToError || nextTimeToError) return;

    onApply({
      user_ids: selectedUserIds,
      date_from: dateFrom,
      date_to: dateTo,
      eStatus: status,
      time_from: status === "CUSTOM" ? timeFrom || null : null,
      time_to: status === "CUSTOM" ? timeTo || null : null,
      languages: [],
    });
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 360, p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6">Bulk Apply Availability</Typography>

        <FormControl fullWidth size="small">
          <FormLabel>Status</FormLabel>
          <Select value={status} onChange={e => setStatus(e.target.value)}>
            <MenuItem value="">Select status</MenuItem>
            {STATUS_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.short} — {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {status === "CUSTOM" && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <TimePickerComponent
              name="time_from"
              label="From"
              value={timeFrom}
              onChange={handleTimeFromChange}
            />
            <TimePickerComponent
              name="time_to"
              label="To"
              value={timeTo}
              onChange={handleTimeToChange}
              error={timeToError}
            />
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 1 }}>
          <DatePickerComponent
            label="From Date"
            value={dateFrom}
            onChange={handleDateFromChange}
          />
          <DatePickerComponent
            label="To Date"
            value={dateTo}
            onChange={handleDateToChange}
            error={dateToError}
          />
        </Box>

        <Box>
          <Button size="small" onClick={selectAll}>
            {selectedUserIds.length === linguists.length ? "Deselect all" : "Select all"}
          </Button>
          <List dense sx={{ maxHeight: 280, overflow: "auto", border: 1, borderColor: "divider" }}>
            {linguists.map(ling => (
              <ListItem key={ling.id} disablePadding>
                <ListItemButton onClick={() => toggleUser(ling.id)}>
                  <ListItemIcon>
                    <Checkbox edge="start" checked={selectedUserIds.includes(ling.id)} tabIndex={-1} />
                  </ListItemIcon>
                  <ListItemText primary={ling.name} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={onClose} fullWidth disabled={applying}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            fullWidth
            disabled={!selectedUserIds.length || !status || applying}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default BulkApplyDrawer;
